<template>
  <AIVDialog
    v-model="isVisible"
    title="Shift Tasks"
    width="50%"
    height="80vh"
    class="group-shift-tasks-modal"
  >
    <!-- Task List View -->
    <div v-if="currentView === 'list'" class="tasks-content">
      <div class="progress-header">
        <span class="progress-text">
          Progress: {{ completedTaskCount }}/{{ totalTaskCount }} tasks completed
        </span>
        <div class="progress-bar-wrapper">
          <div
            class="progress-bar-fill"
            :style="{ width: progressPercent + '%' }"
          ></div>
        </div>
      </div>

      <div v-if="allTasksComplete" class="ready-indicator">
        <i class="fa-solid fa-circle-check"></i>
        You're ready to clock out!
      </div>

      <!-- Pick Ups Card -->
      <div
        class="task-card"
        :class="{ disabled: !hasPickupsAssigned }"
        @click="hasPickupsAssigned && (currentView = 'pickups')"
      >
        <div class="task-card-content">
          <div class="task-card-icon">
            <i class="fa-solid fa-car-side"></i>
          </div>
          <div class="task-card-info">
            <h4>Pick Ups</h4>
            <p v-if="!hasPickupsAssigned" class="locked-message">
              None assigned to you
            </p>
          </div>
          <div class="task-card-status">
            <span
              v-if="hasPickupsAssigned"
              class="task-count"
              :class="{ complete: allMyPickupsComplete }"
            >
              {{ completedPickups }}/{{ totalPickups }} Done
            </span>
            <i
              v-if="hasPickupsAssigned"
              class="fa-solid fa-chevron-right"
            ></i>
          </div>
        </div>
      </div>

      <!-- Drop Offs Card -->
      <div
        class="task-card"
        :class="{ disabled: !hasDropoffsAssigned || !allPickupsComplete }"
        @click="hasDropoffsAssigned && allPickupsComplete && (currentView = 'dropoffs')"
      >
        <div class="task-card-content">
          <div class="task-card-icon">
            <i class="fa-solid fa-location-dot"></i>
          </div>
          <div class="task-card-info">
            <h4>Drop Offs</h4>
            <p v-if="!hasDropoffsAssigned" class="locked-message">
              None assigned to you
            </p>
            <p v-else-if="!allPickupsComplete" class="locked-message">
              Clients must be picked up first
            </p>
          </div>
          <div class="task-card-status">
            <span
              v-if="hasDropoffsAssigned && allPickupsComplete"
              class="task-count"
              :class="{ complete: allDropoffsComplete }"
            >
              {{ completedDropoffs }}/{{ totalDropoffs }} Done
            </span>
            <i
              v-if="hasDropoffsAssigned && allPickupsComplete"
              class="fa-solid fa-chevron-right"
            ></i>
            <i v-else-if="hasDropoffsAssigned" class="fa-solid fa-lock lock-icon"></i>
          </div>
        </div>
      </div>

      <!-- Shift Notes Card -->
      <div
        class="task-card"
        :class="{ disabled: !hasNotesAssigned || !anyNotesClientPickedUp }"
        @click="hasNotesAssigned && anyNotesClientPickedUp && enterNotesView()"
      >
        <div class="task-card-content">
          <div class="task-card-icon">
            <i class="fa-solid fa-clipboard"></i>
          </div>
          <div class="task-card-info">
            <h4>Shift Notes</h4>
            <p v-if="!hasNotesAssigned" class="locked-message">
              None assigned to you
            </p>
            <p v-else-if="!anyNotesClientPickedUp" class="locked-message">
              Clients must be picked up first
            </p>
          </div>
          <div class="task-card-status">
            <span
              v-if="hasNotesAssigned && anyNotesClientPickedUp"
              class="task-count"
              :class="{ complete: allNotesComplete }"
            >
              {{ completedNotes }}/{{ totalNotes }} Done
            </span>
            <i
              v-if="hasNotesAssigned && anyNotesClientPickedUp"
              class="fa-solid fa-chevron-right"
            ></i>
            <i v-else-if="hasNotesAssigned" class="fa-solid fa-lock lock-icon"></i>
          </div>
        </div>
      </div>

      <!-- Medications Card -->
      <div
        class="task-card"
        :class="{ disabled: !hasMedicationsAssigned || !anyMedicationClientPickedUp }"
        @click="hasMedicationsAssigned && anyMedicationClientPickedUp && enterMedicationsView()"
      >
        <div class="task-card-content">
          <div class="task-card-icon">
            <i class="fa-solid fa-pills"></i>
          </div>
          <div class="task-card-info">
            <h4>Medications</h4>
            <p v-if="!hasMedicationsAssigned" class="locked-message">
              No medications for assigned clients
            </p>
            <p v-else-if="!anyMedicationClientPickedUp" class="locked-message">
              Clients must be picked up first
            </p>
          </div>
          <div class="task-card-status">
            <span
              v-if="hasMedicationsAssigned && anyMedicationClientPickedUp"
              class="task-count"
              :class="{ complete: allMedicationsComplete }"
            >
              {{ completedMedicationClients }}/{{ totalMedicationClients }} Done
            </span>
            <i
              v-if="hasMedicationsAssigned && anyMedicationClientPickedUp"
              class="fa-solid fa-chevron-right"
            ></i>
            <i v-else-if="hasMedicationsAssigned" class="fa-solid fa-lock lock-icon"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Pick Ups Detail View -->
    <div v-else-if="currentView === 'pickups'" class="detail-content">
      <div class="detail-header">
        <button class="back-btn" @click="currentView = 'list'">
          <i class="fa-solid fa-arrow-left"></i> Back
        </button>
        <h3>Pick Ups</h3>
      </div>

      <!-- Location Info -->
      <div class="location-info-card">
        <div class="location-row">
          <span class="location-label"><i class="fa-solid fa-user"></i> Your Location:</span>
          <span v-if="currentLocation" class="location-value">
            {{ currentLocation.latitude.toFixed(6) }}, {{ currentLocation.longitude.toFixed(6) }}
          </span>
          <span v-else-if="gettingLocation" class="location-pending">
            <i class="fa-solid fa-spinner fa-spin"></i> Getting your location...
          </span>
          <span v-else class="location-pending">
            Location unavailable
          </span>
        </div>
      </div>

      <div class="client-list">
        <div
          v-for="cd in myPickups"
          :key="'pickup-' + cd.id"
          class="client-task-row"
        >
          <div class="client-task-info">
            <span class="client-name">{{ getClientName(cd) }}</span>
            <span v-if="getPickupAddress(cd)" class="task-address">
              <i class="fa-solid fa-location-dot"></i>
              {{ getPickupAddress(cd) }}
              <el-button
                type="text"
                size="small"
                @click.stop="openMapLink(getPickupAddress(cd))"
                class="map-link-btn"
              >
                View on Map
              </el-button>
            </span>
            <span v-if="cd.did_not_attend" class="task-status did-not-attend">
              <i class="fa-solid fa-user-xmark"></i> Did Not Attend
            </span>
            <span v-else-if="cd.pickup_time" class="task-status completed">
              Picked up at {{ formatTime(cd.pickup_time) }}
              <el-button
                v-if="!isEditingTime(cd, 'pickup')"
                type="text"
                size="mini"
                class="adjust-time-btn"
                @click="startTimeEdit(cd, 'pickup')"
              >
                Adjust time
              </el-button>
            </span>
            <div v-if="isEditingTime(cd, 'pickup')" class="time-edit-row">
              <el-time-picker
                v-model="timeEdit.value"
                size="small"
                format="h:mm A"
                :clearable="false"
              />
              <el-button
                type="primary"
                size="mini"
                :loading="loadingAction === 'adjust-pickup-' + cd.id_clients"
                @click="saveTimeEdit(cd)"
              >
                Save
              </el-button>
              <el-button size="mini" @click="cancelTimeEdit">Cancel</el-button>
            </div>
            <span v-if="cd.pickup_time && !cd.did_not_attend" class="task-geocode">
              <i class="fa-solid fa-location-dot"></i>
              {{ cd.pickup_geocode || 'Location unavailable' }}
            </span>
            <span v-if="!cd.pickup_time && !cd.did_not_attend" class="task-status pending">Pending</span>
          </div>
          <div v-if="!cd.pickup_time && !cd.did_not_attend" class="task-action-buttons">
            <el-button
              type="primary"
              size="small"
              :loading="loadingAction === 'pickup-' + cd.id_clients"
              @click="handleMarkPickup(cd)"
            >
              Mark Picked Up
            </el-button>
            <el-button
              type="danger"
              size="small"
              plain
              :loading="loadingAction === 'dna-' + cd.id_clients"
              @click="handleDidNotAttend(cd)"
            >
              Did Not Attend
            </el-button>
          </div>
          <div v-if="cd.pickup_time && !cd.dropoff_time && !cd.did_not_attend" class="task-action-buttons mt-1">
            <el-button
              type="warning"
              size="small"
              plain
              :loading="loadingAction === 'undo-pickup-' + cd.id_clients"
              @click="handleUndoPickup(cd)"
            >
              Undo Pickup
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Drop Offs Detail View -->
    <div v-else-if="currentView === 'dropoffs'" class="detail-content">
      <div class="detail-header">
        <button class="back-btn" @click="currentView = 'list'">
          <i class="fa-solid fa-arrow-left"></i> Back
        </button>
        <h3>Drop Offs</h3>
      </div>

      <!-- Location Info -->
      <div class="location-info-card">
        <div class="location-row">
          <span class="location-label"><i class="fa-solid fa-user"></i> Your Location:</span>
          <span v-if="currentLocation" class="location-value">
            {{ currentLocation.latitude.toFixed(6) }}, {{ currentLocation.longitude.toFixed(6) }}
          </span>
          <span v-else-if="gettingLocation" class="location-pending">
            <i class="fa-solid fa-spinner fa-spin"></i> Getting your location...
          </span>
          <span v-else class="location-pending">
            Location unavailable
          </span>
        </div>
      </div>

      <div class="client-list">
        <div
          v-for="cd in myDropoffs"
          :key="'dropoff-' + cd.id"
          class="client-task-row"
        >
          <div class="client-task-info">
            <span class="client-name">{{ getClientName(cd) }}</span>
            <span v-if="getDropoffAddress(cd)" class="task-address">
              <i class="fa-solid fa-location-dot"></i>
              {{ getDropoffAddress(cd) }}
              <el-button
                type="text"
                size="small"
                @click.stop="openMapLink(getDropoffAddress(cd))"
                class="map-link-btn"
              >
                View on Map
              </el-button>
            </span>
            <span v-if="cd.did_not_attend" class="task-status did-not-attend">
              <i class="fa-solid fa-user-xmark"></i> Did Not Attend
            </span>
            <span v-else-if="cd.dropoff_time" class="task-status completed">
              Dropped off at {{ formatTime(cd.dropoff_time) }}
              <el-button
                v-if="!isEditingTime(cd, 'dropoff')"
                type="text"
                size="mini"
                class="adjust-time-btn"
                @click="startTimeEdit(cd, 'dropoff')"
              >
                Adjust time
              </el-button>
            </span>
            <div v-if="isEditingTime(cd, 'dropoff')" class="time-edit-row">
              <el-time-picker
                v-model="timeEdit.value"
                size="small"
                format="h:mm A"
                :clearable="false"
              />
              <el-button
                type="primary"
                size="mini"
                :loading="loadingAction === 'adjust-dropoff-' + cd.id_clients"
                @click="saveTimeEdit(cd)"
              >
                Save
              </el-button>
              <el-button size="mini" @click="cancelTimeEdit">Cancel</el-button>
            </div>
            <span v-if="cd.dropoff_time && !cd.did_not_attend" class="task-geocode">
              <i class="fa-solid fa-location-dot"></i>
              {{ cd.dropoff_geocode || 'Location unavailable' }}
            </span>
            <span v-else-if="!cd.pickup_time && !cd.did_not_attend" class="task-status locked">
              (Pick up first)
            </span>
            <span v-if="!cd.dropoff_time && cd.pickup_time && !cd.did_not_attend" class="task-status pending">Pending</span>
          </div>
          <div v-if="!cd.dropoff_time && cd.pickup_time && !cd.did_not_attend" class="task-action-buttons">
            <el-button
              type="success"
              size="small"
              :loading="loadingAction === 'dropoff-' + cd.id_clients"
              @click="handleMarkDropoff(cd)"
            >
              Mark Dropped Off
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Shift Notes Detail View -->
    <div v-else-if="currentView === 'notes'" class="detail-content">
      <div class="detail-header">
        <button class="back-btn" @click="currentView = 'list'">
          <i class="fa-solid fa-arrow-left"></i> Back
        </button>
        <h3>Shift Notes</h3>
      </div>

      <div v-if="loadingNotes" class="notes-loading">
        <i class="fa-solid fa-spinner fa-spin"></i> Loading questions...
      </div>

      <div v-else class="notes-client-list">
        <div
          v-for="cd in myNotes"
          :key="'notes-' + cd.id"
          class="notes-client-section"
        >
          <div
            class="notes-client-header"
            @click="!cd.did_not_attend && toggleClientNotes(cd.id_clients)"
          >
            <i
              v-if="!cd.did_not_attend"
              class="fa-solid"
              :class="expandedClients[cd.id_clients] ? 'fa-chevron-down' : 'fa-chevron-right'"
            ></i>
            <i v-else class="fa-solid fa-user-xmark" style="color: #f56c6c; width: 16px; text-align: center;"></i>
            <span class="client-name">{{ getClientName(cd) }}</span>
            <span
              v-if="cd.did_not_attend"
              class="task-status did-not-attend ml-auto"
            >
              <i class="fa-solid fa-user-xmark"></i> Did Not Attend
            </span>
            <span
              v-else-if="cd.shift_notes_completed"
              class="task-status completed ml-auto"
            >
              <i class="fa-solid fa-check"></i> Completed
            </span>
            <span v-else-if="!cd.pickup_time" class="task-status locked ml-auto">
              <i class="fa-solid fa-lock"></i> Awaiting pickup
            </span>
            <span v-else class="task-status pending ml-auto">Pending</span>
          </div>

          <div v-if="expandedClients[cd.id_clients] && !cd.did_not_attend" class="notes-client-body">
            <!-- Service Code Questions -->
            <div
              v-for="q in serviceCodeQuestions"
              :key="'scq-' + q.id"
              class="notes-section"
            >
              <label class="notes-label">
                {{ q.question_text }}
                <span v-if="q.required" class="required-mark">*</span>
              </label>
              <el-input
                v-if="q.question_type === 'free_response'"
                v-model="notesData[cd.id_clients].questionAnswers[q.id]"
                type="textarea"
                :rows="2"
                placeholder="Enter your answer..."
                :disabled="!cd.pickup_time"
              ></el-input>
              <div v-else-if="q.question_type === 'yes_no'" class="yes-no-buttons">
                <el-button
                  :type="notesData[cd.id_clients].questionAnswers[q.id] === 'yes' ? 'primary' : 'default'"
                  size="medium"
                  :disabled="!cd.pickup_time"
                  @click="setAnswer(cd.id_clients, 'questionAnswers', q.id, 'yes')"
                >Yes</el-button>
                <el-button
                  :type="notesData[cd.id_clients].questionAnswers[q.id] === 'no' ? 'danger' : 'default'"
                  size="medium"
                  :disabled="!cd.pickup_time"
                  @click="setAnswer(cd.id_clients, 'questionAnswers', q.id, 'no')"
                >No</el-button>
              </div>
            </div>

            <!-- Client Goal Questions -->
            <div
              v-for="goal in clientGoals[cd.id_clients] || []"
              :key="'goal-' + goal.id"
              class="notes-section"
            >
              <label class="notes-label">{{ goal.question_text }}</label>
              <el-input
                v-if="goal.question_type === 'free_response'"
                v-model="notesData[cd.id_clients].goalAnswers[goal.id]"
                type="textarea"
                :rows="2"
                placeholder="Enter your answer..."
                :disabled="!cd.pickup_time"
              ></el-input>
              <div v-else-if="goal.question_type === 'yes_no'" class="yes-no-buttons">
                <el-button
                  :type="notesData[cd.id_clients].goalAnswers[goal.id] === 'yes' ? 'primary' : 'default'"
                  size="medium"
                  :disabled="!cd.pickup_time"
                  @click="setAnswer(cd.id_clients, 'goalAnswers', goal.id, 'yes')"
                >Yes</el-button>
                <el-button
                  :type="notesData[cd.id_clients].goalAnswers[goal.id] === 'no' ? 'danger' : 'default'"
                  size="medium"
                  :disabled="!cd.pickup_time"
                  @click="setAnswer(cd.id_clients, 'goalAnswers', goal.id, 'no')"
                >No</el-button>
              </div>
            </div>

            <!-- Concerns -->
            <div class="notes-section">
              <label class="notes-label">Do you have any concerns about this individual today? <span class="required-mark">*</span></label>
              <el-input
                v-model="notesData[cd.id_clients].shiftNotes"
                type="textarea"
                :rows="3"
                placeholder="Enter your answer..."
                :disabled="!cd.pickup_time"
              ></el-input>
            </div>

            <!-- Save Button -->
            <div v-if="cd.pickup_time" class="notes-save-row">
              <el-button
                type="primary"
                :loading="loadingAction === 'notes-' + cd.id_clients"
                @click="handleSaveNotes(cd)"
              >
                {{ cd.shift_notes_completed ? 'Update Notes' : 'Save Notes' }}
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Medications Detail View -->
    <div v-else-if="currentView === 'medications'" class="detail-content">
      <div class="detail-header">
        <button class="back-btn" @click="currentView = 'list'">
          <i class="fa-solid fa-arrow-left"></i> Back
        </button>
        <h3>Medications</h3>
      </div>

      <div v-if="loadingMedications" class="notes-loading">
        <i class="fa-solid fa-spinner fa-spin"></i> Loading medications...
      </div>

      <div v-else class="notes-client-list">
        <div
          v-for="cd in myMedicationClients"
          :key="'med-' + cd.id"
          class="notes-client-section"
        >
          <!-- Only render sections for clients that have medications -->
          <template v-if="clientMedications[cd.id_clients] && clientMedications[cd.id_clients].medications.length > 0">
            <div
              class="notes-client-header"
              @click="!cd.did_not_attend && toggleMedicationClient(cd.id_clients)"
            >
              <i
                v-if="!cd.did_not_attend"
                class="fa-solid"
                :class="medicationExpandedClients[cd.id_clients] ? 'fa-chevron-down' : 'fa-chevron-right'"
              ></i>
              <i v-else class="fa-solid fa-user-xmark" style="color: #f56c6c; width: 16px; text-align: center;"></i>
              <span class="client-name">{{ getClientName(cd) }}</span>
              <span
                v-if="cd.did_not_attend"
                class="task-status did-not-attend ml-auto"
              >
                <i class="fa-solid fa-user-xmark"></i> Did Not Attend
              </span>
              <span
                v-else-if="medicationSavedClients[cd.id_clients]"
                class="task-status completed ml-auto"
              >
                <i class="fa-solid fa-check"></i> Saved
              </span>
              <span v-else-if="!cd.pickup_time" class="task-status locked ml-auto">
                <i class="fa-solid fa-lock"></i> Awaiting pickup
              </span>
              <span v-else class="task-status pending ml-auto">Pending</span>
            </div>

            <div v-if="medicationExpandedClients[cd.id_clients]" class="notes-client-body">
              <div
                v-for="med in clientMedications[cd.id_clients].medications"
                :key="`med-item-${cd.id_clients}-${med.id}`"
                class="medication-admin-item mb-2"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ med.name }}</strong>
                    <span v-if="med.dosage"> - {{ med.dosage }}</span>
                    <span v-if="med.frequency"> ({{ med.frequency }})</span>
                  </div>
                  <el-switch
                    v-model="medicationAdministrations[`${cd.id_clients}_${med.id}`].wasAdministered"
                    active-text="Given"
                    inactive-text="Skipped"
                    :disabled="!cd.pickup_time"
                  />
                </div>
                <div v-if="!medicationAdministrations[`${cd.id_clients}_${med.id}`].wasAdministered" class="mt-1">
                  <el-input
                    v-model="medicationAdministrations[`${cd.id_clients}_${med.id}`].skipReason"
                    placeholder="Reason for skipping (required)"
                    size="small"
                    :disabled="!cd.pickup_time"
                  />
                </div>
                <div v-if="medicationAdministrations[`${cd.id_clients}_${med.id}`].wasAdministered" class="mt-1">
                  <span style="color: #e0e0e0; font-size: 0.85rem;">Time Given:</span>
                  <AIVTimeSelect
                    v-model="medicationAdministrations[`${cd.id_clients}_${med.id}`].administeredAtTime"
                    :minute-interval="5"
                    :allow-exact-minutes="true"
                    placeholder="Time given"
                  />
                </div>
              </div>

              <!-- Per-client Save button -->
              <div v-if="cd.pickup_time" class="notes-save-row">
                <el-button
                  type="primary"
                  :loading="loadingAction === 'med-' + cd.id_clients"
                  @click="handleSaveMedications(cd)"
                >
                  {{ medicationSavedClients[cd.id_clients] ? 'Update Medications' : 'Save Medications' }}
                </el-button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleClose">Close</el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "./AIVDialog.vue";
import AIVTimeSelect from "./AIVTimeSelect.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { shiftNoteQuestionsService } from "@/serviceHandlers/shiftNoteQuestionsService";
import { clientsService } from "@/serviceHandlers/clientsService";

export default {
  name: "GroupShiftTasksModal",
  components: {
    AIVDialog,
    AIVTimeSelect,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: null,
    },
    userId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      isVisible: this.visible,
      currentView: "list",
      gettingLocation: false,
      currentLocation: null,
      loadingAction: null,
      // Inline pickup/dropoff time adjustment: { clientId, event, value }
      timeEdit: null,
      // Shift notes data
      loadingNotes: false,
      notesFetched: false,
      serviceCodeQuestions: [],
      clientGoals: {},
      notesData: {},
      expandedClients: {},
      // Medications data
      medicationsFetched: false,
      loadingMedications: false,
      clientMedications: {},
      medicationAdministrations: {},
      medicationExpandedClients: {},
      medicationSavedClients: {},
    };
  },
  watch: {
    visible(newVal) {
      this.isVisible = newVal;
      if (newVal) {
        this.currentView = "list";
        this.getCurrentLocation();
        // Reset notes data so it re-fetches with fresh shift data
        this.notesFetched = false;
        this.notesData = {};
        this.expandedClients = {};
        // Reset medications data
        this.medicationsFetched = false;
        this.clientMedications = {};
        this.medicationAdministrations = {};
        this.medicationExpandedClients = {};
        this.medicationSavedClients = {};
        // Pre-fetch medications data so counts are accurate on the main list view
        this.fetchMedicationsData();
      }
    },
    isVisible(newVal) {
      if (!newVal) {
        this.$emit("update:visible", false);
      }
    },
  },
  computed: {
    clientDetails() {
      if (!this.shift || !this.shift.clientDetails) return [];
      return this.shift.clientDetails;
    },
    numericUserId() {
      return Number(this.userId);
    },
    shiftServiceCode() {
      if (!this.shift || !this.shift.service_codes) return "";
      const sc = this.shift.service_codes.find(
        (s) => s.code === "DSG" || s.code === "DSP",
      );
      return sc ? sc.code : "";
    },
    myPickups() {
      return this.clientDetails.filter(
        (cd) => Number(cd.pickup_caregiver_id) === this.numericUserId,
      );
    },
    myDropoffs() {
      return this.clientDetails.filter(
        (cd) => Number(cd.dropoff_caregiver_id) === this.numericUserId,
      );
    },
    myNotes() {
      return this.clientDetails.filter(
        (cd) => Number(cd.shift_notes_caregiver_id) === this.numericUserId,
      );
    },
    myMedicationClients() {
      return this.clientDetails.filter(
        (cd) => Number(cd.medications_caregiver_id || cd.shift_notes_caregiver_id) === this.numericUserId,
      );
    },
    totalPickups() {
      return this.myPickups.length;
    },
    completedPickups() {
      return this.myPickups.filter((cd) => cd.pickup_time || cd.did_not_attend).length;
    },
    hasPickupsAssigned() {
      return this.totalPickups > 0;
    },
    allMyPickupsComplete() {
      return this.totalPickups > 0 && this.completedPickups === this.totalPickups;
    },
    allPickupsComplete() {
      // Check if ALL clients assigned to my dropoffs have been picked up (by any caregiver) or marked did not attend
      const myDropoffClientIds = this.myDropoffs.map((cd) => cd.id_clients);
      const allMyDropoffClientsPickedUp = myDropoffClientIds.every((clientId) => {
        const detail = this.clientDetails.find((cd) => cd.id_clients === clientId);
        return detail && (detail.pickup_time || detail.did_not_attend);
      });
      // Unlocked if: my own pickups are done (or I have none) AND all clients I need to drop off have been picked up
      const myPickupsDone = this.totalPickups === 0 || this.completedPickups === this.totalPickups;
      return myPickupsDone && allMyDropoffClientsPickedUp;
    },
    totalDropoffs() {
      return this.myDropoffs.length;
    },
    completedDropoffs() {
      return this.myDropoffs.filter((cd) => cd.dropoff_time || cd.did_not_attend).length;
    },
    hasDropoffsAssigned() {
      return this.totalDropoffs > 0;
    },
    hasNotesAssigned() {
      return this.totalNotes > 0;
    },
    allDropoffsComplete() {
      return this.totalDropoffs > 0 && this.completedDropoffs === this.totalDropoffs;
    },
    totalNotes() {
      return this.myNotes.length;
    },
    completedNotes() {
      return this.myNotes.filter((cd) => cd.shift_notes_completed || cd.did_not_attend).length;
    },
    anyNotesClientPickedUp() {
      return this.myNotes.some((cd) => cd.pickup_time || cd.did_not_attend);
    },
    allNotesComplete() {
      return this.totalNotes > 0 && this.completedNotes === this.totalNotes;
    },
    hasMedicationsAssigned() {
      if (!this.medicationsFetched) return this.myMedicationClients.length > 0;
      return this.myMedicationClients.some(
        (cd) => this.clientMedications[cd.id_clients] && this.clientMedications[cd.id_clients].medications.length > 0
      );
    },
    totalMedicationClients() {
      if (!this.medicationsFetched) return 0;
      return this.myMedicationClients.filter(
        (cd) => this.clientMedications[cd.id_clients] && this.clientMedications[cd.id_clients].medications.length > 0
      ).length;
    },
    completedMedicationClients() {
      return this.myMedicationClients.filter(
        (cd) =>
          (this.clientMedications[cd.id_clients] && this.clientMedications[cd.id_clients].medications.length > 0) &&
          (this.medicationSavedClients[cd.id_clients] === true || cd.did_not_attend)
      ).length;
    },
    allMedicationsComplete() {
      return this.totalMedicationClients > 0 && this.completedMedicationClients === this.totalMedicationClients;
    },
    anyMedicationClientPickedUp() {
      return this.myMedicationClients.some((cd) => cd.pickup_time || cd.did_not_attend);
    },
    totalTaskCount() {
      return this.totalPickups + this.totalDropoffs + this.totalNotes + this.totalMedicationClients;
    },
    completedTaskCount() {
      return this.completedPickups + this.completedDropoffs + this.completedNotes + this.completedMedicationClients;
    },
    progressPercent() {
      if (this.totalTaskCount === 0) return 0;
      return Math.round((this.completedTaskCount / this.totalTaskCount) * 100);
    },
    allTasksComplete() {
      return (
        this.totalTaskCount > 0 &&
        this.completedTaskCount === this.totalTaskCount
      );
    },
  },
  methods: {
    async getCurrentLocation() {
      if (this.gettingLocation || this.currentLocation) return;

      this.gettingLocation = true;
      try {
        if (!navigator.geolocation) return;

        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        });

        this.currentLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
      } catch (error) {
        // Location not available - continue without it
      } finally {
        this.gettingLocation = false;
      }
    },
    getClientName(clientDetail) {
      if (clientDetail.client) {
        const c = clientDetail.client;
        return `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Unknown Client";
      }
      return "Unknown Client";
    },
    getClientAddress(clientDetail) {
      const addr = clientDetail.address;
      if (!addr) return null;
      const parts = [];
      if (addr.address1) parts.push(addr.address1);
      if (addr.address2) parts.push(addr.address2);
      if (addr.city) parts.push(addr.city);
      if (addr.state) parts.push(addr.state);
      if (addr.zip) parts.push(addr.zip);
      return parts.length > 0 ? parts.join(", ") : null;
    },
    formatAddr(addr) {
      if (!addr) return null;
      const parts = [];
      if (addr.address1) parts.push(addr.address1);
      if (addr.address2) parts.push(addr.address2);
      if (addr.city) parts.push(addr.city);
      if (addr.state) parts.push(addr.state);
      if (addr.zip) parts.push(addr.zip);
      return parts.length > 0 ? parts.join(", ") : null;
    },
    getPickupAddress(clientDetail) {
      return this.formatAddr(clientDetail.pickupAddress) || this.getClientAddress(clientDetail);
    },
    getDropoffAddress(clientDetail) {
      return this.formatAddr(clientDetail.dropoffAddress) || this.getClientAddress(clientDetail);
    },
    openMapLink(address) {
      const encodedAddress = encodeURIComponent(address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    },
    formatTime(datetime) {
      if (!datetime) return "";
      const date = new Date(datetime);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
    getGeocode() {
      if (this.currentLocation) {
        return `${this.currentLocation.latitude},${this.currentLocation.longitude}`;
      }
      return null;
    },
    async handleMarkPickup(clientDetail) {
      const clientName = this.getClientName(clientDetail);
      try {
        await this.$confirm(
          `Are you sure you're picking up ${clientName}?`,
          "Confirm Pickup",
          {
            confirmButtonText: "Yes, Picked Up",
            cancelButtonText: "Cancel",
            type: "info",
          },
        );
      } catch {
        return; // User cancelled
      }

      this.loadingAction = "pickup-" + clientDetail.id_clients;
      try {
        const payload = {
          shiftId: this.shift.id,
          clientId: clientDetail.id_clients,
          caregiverId: this.numericUserId,
          geocode: this.getGeocode(),
          utcTime: new Date().toISOString(),
        };

        await shiftsService.markPickup(payload);
        clientDetail.pickup_time = new Date().toISOString();
        clientDetail.pickup_geocode = this.getGeocode();
        this.$notifySuccess("Client marked as picked up!");
        this.$emit("refresh");
      } catch (error) {
        this.$notifyError(
          "Error marking pickup: " +
            (error.response?.data?.error || error.message),
        );
      } finally {
        this.loadingAction = null;
      }
    },
    isEditingTime(clientDetail, event) {
      return (
        !!this.timeEdit &&
        this.timeEdit.clientId === clientDetail.id_clients &&
        this.timeEdit.event === event
      );
    },
    startTimeEdit(clientDetail, event) {
      const current =
        event === "pickup" ? clientDetail.pickup_time : clientDetail.dropoff_time;
      this.timeEdit = {
        clientId: clientDetail.id_clients,
        event,
        value: current ? new Date(current) : new Date(),
      };
    },
    cancelTimeEdit() {
      this.timeEdit = null;
    },
    async saveTimeEdit(clientDetail) {
      if (!this.timeEdit || !this.timeEdit.value) return;
      const { event } = this.timeEdit;
      const field = event === "pickup" ? "pickup_time" : "dropoff_time";
      // Keep the original date; the picker only changes the time of day.
      const adjusted = new Date(clientDetail[field]);
      const picked = new Date(this.timeEdit.value);
      adjusted.setHours(picked.getHours(), picked.getMinutes(), 0, 0);

      this.loadingAction = `adjust-${event}-` + clientDetail.id_clients;
      try {
        await shiftsService.updateClientEventTime({
          shiftId: this.shift.id,
          clientId: clientDetail.id_clients,
          caregiverId: this.numericUserId,
          event,
          utcTime: adjusted.toISOString(),
        });
        clientDetail[field] = adjusted.toISOString();
        this.timeEdit = null;
        this.$notifySuccess(
          `${event === "pickup" ? "Pickup" : "Drop-off"} time updated`,
        );
        this.$emit("refresh");
      } catch (error) {
        this.$notifyError(
          "Error updating time: " +
            (error.response?.data?.error || error.message),
        );
      } finally {
        this.loadingAction = null;
      }
    },
    async handleUndoPickup(clientDetail) {
      const clientName = this.getClientName(clientDetail);
      try {
        await this.$confirm(
          `Are you sure you want to undo the pickup for ${clientName}?`,
          "Undo Pickup",
          {
            confirmButtonText: "Yes, Undo",
            cancelButtonText: "Cancel",
            type: "warning",
          },
        );
      } catch {
        return;
      }

      this.loadingAction = "undo-pickup-" + clientDetail.id_clients;
      try {
        const payload = {
          shiftId: this.shift.id,
          clientId: clientDetail.id_clients,
          caregiverId: this.numericUserId,
        };
        await shiftsService.undoPickup(payload);
        clientDetail.pickup_time = null;
        clientDetail.pickup_geocode = null;
        clientDetail.pickup_notes = null;
        this.$notifySuccess("Pickup undone successfully");
        this.$emit("refresh");
      } catch (error) {
        this.$notifyError(
          "Error undoing pickup: " +
            (error.response?.data?.error || error.message),
        );
      } finally {
        this.loadingAction = null;
      }
    },
    async handleDidNotAttend(clientDetail) {
      const clientName = this.getClientName(clientDetail);
      try {
        await this.$confirm(
          `Are you sure you want to mark ${clientName} as "Did Not Attend"? This will mark all tasks for this client as complete.`,
          "Confirm Did Not Attend",
          {
            confirmButtonText: "Yes, Did Not Attend",
            cancelButtonText: "Cancel",
            type: "warning",
          },
        );
      } catch {
        // User cancelled the confirmation
        return;
      }

      this.loadingAction = "dna-" + clientDetail.id_clients;
      try {
        const payload = {
          shiftId: this.shift.id,
          clientId: clientDetail.id_clients,
          caregiverId: this.numericUserId,
        };

        await shiftsService.markDidNotAttend(payload);
        this.$set(clientDetail, 'did_not_attend', true);
        this.$notifySuccess(`${clientName} marked as Did Not Attend.`);
        this.$emit("refresh");
      } catch (error) {
        this.$notifyError(
          "Error marking did not attend: " +
            (error.response?.data?.error || error.message),
        );
      } finally {
        this.loadingAction = null;
      }
    },
    async handleMarkDropoff(clientDetail) {
      this.loadingAction = "dropoff-" + clientDetail.id_clients;
      try {
        const payload = {
          shiftId: this.shift.id,
          clientId: clientDetail.id_clients,
          caregiverId: this.numericUserId,
          geocode: this.getGeocode(),
          utcTime: new Date().toISOString(),
        };

        await shiftsService.markDropoff(payload);
        clientDetail.dropoff_time = new Date().toISOString();
        clientDetail.dropoff_geocode = this.getGeocode();
        this.$notifySuccess("Client marked as dropped off!");
        this.$emit("refresh");
      } catch (error) {
        this.$notifyError(
          "Error marking dropoff: " +
            (error.response?.data?.error || error.message),
        );
      } finally {
        this.loadingAction = null;
      }
    },

    // --- Shift Notes Methods ---
    async enterNotesView() {
      this.currentView = "notes";
      if (!this.notesFetched) {
        await this.fetchNotesData();
      }
    },
    async fetchNotesData() {
      this.loadingNotes = true;
      try {
        // Fetch service code questions (shared across all clients)
        if (this.shiftServiceCode) {
          try {
            this.serviceCodeQuestions =
              await shiftNoteQuestionsService.getByServiceCode(this.shiftServiceCode);
          } catch (e) {
            this.serviceCodeQuestions = [];
          }
        }

        // Fetch client goals for each assigned client
        const goalsPromises = this.myNotes.map(async (cd) => {
          try {
            const response = await clientsService.getGoals(cd.id_clients);
            this.$set(this.clientGoals, cd.id_clients, response.goals || []);
          } catch (e) {
            this.$set(this.clientGoals, cd.id_clients, []);
          }
        });
        await Promise.all(goalsPromises);

        // Fetch existing saved answers from the server
        let taskData = {};
        try {
          taskData = await shiftsService.getShiftTasks(this.shift.id);
        } catch (e) {
          taskData = {};
        }

        // Initialize notesData for each client
        this.myNotes.forEach((cd) => {
          if (!this.notesData[cd.id_clients]) {
            const questionAnswers = {};
            this.serviceCodeQuestions.forEach((q) => {
              questionAnswers[q.id] = "";
            });

            const goalAnswers = {};
            const goals = this.clientGoals[cd.id_clients] || [];
            goals.forEach((g) => {
              goalAnswers[g.id] = "";
            });

            this.$set(this.notesData, cd.id_clients, {
              shiftNotes: "",
              questionAnswers,
              goalAnswers,
            });
          }
        });

        // Load existing shift notes text from client details
        const serverClientDetails = taskData.clientDetails || [];
        serverClientDetails.forEach((cd) => {
          if (cd.shift_notes && this.notesData[cd.id_clients]) {
            this.notesData[cd.id_clients].shiftNotes = cd.shift_notes;
          }
        });

        // Load existing question answers
        const allQuestionAnswers = taskData.shiftNoteAnswers || [];
        for (const answer of allQuestionAnswers) {
          const cId = answer.client_id;
          if (this.notesData[cId]) {
            this.$set(this.notesData[cId].questionAnswers, answer.question_id, answer.answer || "");
          }
        }

        // Build goal-to-client mapping so goal answers go under the correct client
        const goalToClient = {};
        for (const [cId, goalsList] of Object.entries(this.clientGoals)) {
          for (const goal of goalsList) {
            goalToClient[goal.id] = Number(cId);
          }
        }

        // Load existing goal answers
        const allGoalAnswers = taskData.goalAnswers || [];
        for (const answer of allGoalAnswers) {
          const ownerClientId = goalToClient[answer.id_goals];
          if (ownerClientId && this.notesData[ownerClientId]) {
            this.$set(this.notesData[ownerClientId].goalAnswers, answer.id_goals, answer.answer || "");
          }
        }

        // Expand the first incomplete client by default
        const firstIncomplete = this.myNotes.find(
          (cd) => !cd.shift_notes_completed,
        );
        if (firstIncomplete) {
          this.$set(this.expandedClients, firstIncomplete.id_clients, true);
        }

        this.notesFetched = true;
      } catch (error) {
        this.$notifyError(
          "Error loading shift notes data: " +
            (error.response?.data?.error || error.message),
        );
      } finally {
        this.loadingNotes = false;
      }
    },
    toggleClientNotes(clientId) {
      this.$set(this.expandedClients, clientId, !this.expandedClients[clientId]);
    },
    validateNotes(clientDetail) {
      const clientId = clientDetail.id_clients;
      const data = this.notesData[clientId];

      if (!data) return "Notes data not found.";

      // Validate all service code questions
      for (const q of this.serviceCodeQuestions) {
        const answer = data.questionAnswers[q.id];
        if (q.question_type === "yes_no") {
          if (answer !== "yes" && answer !== "no") {
            return `Please select Yes or No for: "${q.question_text}"`;
          }
        } else if (q.question_type === "free_response") {
          if (!answer || answer.trim().length < 20) {
            return `Response must be at least 20 characters for: "${q.question_text}"`;
          }
        }
      }

      // Validate all client goal questions
      const goals = this.clientGoals[clientId] || [];
      for (const goal of goals) {
        const answer = data.goalAnswers[goal.id];
        if (goal.question_type === "yes_no") {
          if (answer !== "yes" && answer !== "no") {
            return `Please select Yes or No for: "${goal.question_text}"`;
          }
        } else if (goal.question_type === "free_response") {
          if (!answer || answer.trim().length < 20) {
            return `Response must be at least 20 characters for: "${goal.question_text}"`;
          }
        }
      }

      // Validate general shift notes (concerns)
      if (!data.shiftNotes || data.shiftNotes.trim().length < 20) {
        return `Response must be at least 20 characters for: "Do you have any concerns about this individual today?"`;
      }

      return null;
    },
    async handleSaveNotes(clientDetail) {
      const validationError = this.validateNotes(clientDetail);
      if (validationError) {
        this.$notifyError(validationError);
        return;
      }

      const clientId = clientDetail.id_clients;
      this.loadingAction = "notes-" + clientId;

      try {
        const data = this.notesData[clientId];

        // Build question answers array
        const questionAnswers = Object.entries(data.questionAnswers)
          .filter(([, answer]) => answer && answer.toString().trim())
          .map(([questionId, answer]) => ({
            questionId: Number(questionId),
            answer: answer.toString().trim(),
          }));

        // Build goal answers array
        const goalAnswers = Object.entries(data.goalAnswers)
          .filter(([, answer]) => answer && answer.toString().trim())
          .map(([goalId, answer]) => ({
            goalId: Number(goalId),
            answer: answer.toString().trim(),
          }));

        await shiftsService.completeShiftNotes({
          shiftId: this.shift.id,
          clientId: clientId,
          caregiverId: this.numericUserId,
          shiftNotes: data.shiftNotes.trim(),
          questionAnswers,
          goalAnswers,
        });

        clientDetail.shift_notes_completed = true;
        this.$notifySuccess("Shift notes saved successfully!");
        this.$emit("refresh");
      } catch (error) {
        this.$notifyError(
          "Error saving shift notes: " +
            (error.response?.data?.error || error.message),
        );
      } finally {
        this.loadingAction = null;
      }
    },
    setAnswer(clientId, answerType, questionId, value) {
      this.$set(this.notesData[clientId][answerType], questionId, value);
    },

    // --- Medications Methods ---
    async enterMedicationsView() {
      this.currentView = "medications";
      if (!this.medicationsFetched) {
        await this.fetchMedicationsData();
      }
    },

    async fetchMedicationsData() {
      this.loadingMedications = true;
      try {
        const now = new Date();
        const currentTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

        // Fetch existing medication administration logs from the backend
        let existingLogs = [];
        try {
          const taskData = await shiftsService.getShiftTasks(this.shift.id);
          existingLogs = taskData.medicationAdministrationLogs || [];
        } catch (e) {
          existingLogs = [];
        }

        // Build a lookup of existing logs by clientId_medicationId
        const existingLogsByKey = {};
        for (const log of existingLogs) {
          const key = `${log.id_clients}_${log.id_medications}`;
          existingLogsByKey[key] = log;
        }

        for (const cd of this.myMedicationClients) {
          if (!cd.id_clients) continue;
          const clientName = this.getClientName(cd);
          try {
            const response = await clientsService.getMedications(cd.id_clients);
            const meds = (response.medications || []).filter(m => m.active !== false);
            this.$set(this.clientMedications, cd.id_clients, { clientName, medications: meds });

            // Initialize administration entries for each medication,
            // pre-populating with existing saved data if available
            let hasExistingLogs = false;
            for (const med of meds) {
              const key = `${cd.id_clients}_${med.id}`;
              const existing = existingLogsByKey[key];
              if (existing) {
                hasExistingLogs = true;
                // Convert administered_at datetime to local HH:MM time string
                let timeStr = currentTimeStr;
                if (existing.administered_at) {
                  const dt = new Date(existing.administered_at);
                  timeStr = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
                }
                this.$set(this.medicationAdministrations, key, {
                  wasAdministered: !!existing.was_administered,
                  skipReason: existing.skip_reason || "",
                  administeredAtTime: timeStr,
                  clientId: cd.id_clients,
                  medicationId: med.id,
                });
              } else if (!this.medicationAdministrations[key]) {
                this.$set(this.medicationAdministrations, key, {
                  wasAdministered: false,
                  skipReason: "",
                  administeredAtTime: currentTimeStr,
                  clientId: cd.id_clients,
                  medicationId: med.id,
                });
              }
            }
            // Mark client as saved if they have existing administration logs
            if (hasExistingLogs) {
              this.$set(this.medicationSavedClients, cd.id_clients, true);
            }
          } catch (e) {
            this.$set(this.clientMedications, cd.id_clients, { clientName, medications: [] });
          }
        }

        // Expand the first client that has medications and hasn't been saved
        const firstPending = this.myMedicationClients.find(
          (cd) =>
            this.clientMedications[cd.id_clients] &&
            this.clientMedications[cd.id_clients].medications.length > 0 &&
            !this.medicationSavedClients[cd.id_clients] &&
            cd.pickup_time
        );
        if (firstPending) {
          this.$set(this.medicationExpandedClients, firstPending.id_clients, true);
        }

        this.medicationsFetched = true;
      } catch (error) {
        this.$notifyError(
          "Error loading medications: " +
            (error.response?.data?.error || error.message)
        );
      } finally {
        this.loadingMedications = false;
      }
    },

    toggleMedicationClient(clientId) {
      this.$set(this.medicationExpandedClients, clientId, !this.medicationExpandedClients[clientId]);
    },

    validateMedications(clientDetail) {
      const clientId = clientDetail.id_clients;
      const meds = (this.clientMedications[clientId] || {}).medications || [];

      for (const med of meds) {
        const key = `${clientId}_${med.id}`;
        const ma = this.medicationAdministrations[key];
        if (!ma) continue;

        if (!ma.wasAdministered && !ma.skipReason.trim()) {
          return `Please provide a reason for skipping "${med.name}"`;
        }
        if (ma.wasAdministered && !ma.administeredAtTime) {
          return `Please select a time for "${med.name}"`;
        }
      }
      return null;
    },

    buildAdministeredAtISO(timeStr) {
      if (!timeStr) return new Date().toISOString();
      const [hours, minutes] = timeStr.split(':').map(Number);
      const dt = new Date();
      if (this.shift?.date) {
        const shiftDateStr = this.shift.date.split('T')[0];
        const [y, mo, d] = shiftDateStr.split('-').map(Number);
        dt.setFullYear(y, mo - 1, d);
      }
      dt.setHours(hours, minutes, 0, 0);
      return dt.toISOString();
    },

    async handleSaveMedications(clientDetail) {
      const validationError = this.validateMedications(clientDetail);
      if (validationError) {
        this.$notifyError(validationError);
        return;
      }

      const clientId = clientDetail.id_clients;
      this.loadingAction = "med-" + clientId;

      try {
        const meds = (this.clientMedications[clientId] || {}).medications || [];
        const medicationAdministrations = meds.map((med) => {
          const key = `${clientId}_${med.id}`;
          const ma = this.medicationAdministrations[key];
          return {
            clientId,
            medicationId: med.id,
            wasAdministered: ma.wasAdministered,
            skipReason: ma.skipReason || null,
            administeredAt: ma.wasAdministered ? this.buildAdministeredAtISO(ma.administeredAtTime) : null,
          };
        });

        await shiftsService.completeMedications({
          shiftId: this.shift.id,
          clientId,
          caregiverId: this.numericUserId,
          medicationAdministrations,
        });

        this.$set(this.medicationSavedClients, clientId, true);
        this.$notifySuccess("Medications saved successfully!");

        // Emit flat map (medId → admin record) for TopNavbar to merge into midShiftMedications
        const flatMap = {};
        for (const ma of medicationAdministrations) {
          flatMap[ma.medicationId] = {
            wasAdministered: ma.wasAdministered,
            skipReason: ma.skipReason,
            administeredAt: ma.administeredAt,
          };
        }
        this.$emit("medications-saved", flatMap);
        this.$emit("refresh");
      } catch (error) {
        this.$notifyError(
          "Error saving medications: " +
            (error.response?.data?.error || error.message),
        );
      } finally {
        this.loadingAction = null;
      }
    },

    handleClose() {
      this.isVisible = false;
    },
  },
};
</script>

<style scoped>

.tasks-content {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.progress-header {
  margin-bottom: 0.5rem;
}

.progress-text {
  font-size: 14px;
  color: #909399;
  display: block;
  margin-bottom: 0.5rem;
}

.progress-bar-wrapper {
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: #67c23a;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.ready-indicator {
  background: rgba(103, 194, 58, 0.15);
  border: 1px solid rgba(103, 194, 58, 0.3);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: #67c23a;
  font-weight: 500;
  text-align: center;
  font-size: 15px;
}

.ready-indicator i {
  margin-right: 0.5rem;
}

.task-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1.25rem;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.task-card:hover:not(.disabled) {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.task-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.task-card-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.task-card-icon {
  font-size: 20px;
  color: #909399;
  width: 32px;
  text-align: center;
}

.task-card-info {
  flex: 1;
}

.task-card-info h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
}

.locked-message {
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #909399;
  font-style: italic;
}

.task-card-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.task-count {
  font-size: 14px;
  color: #909399;
  font-weight: 500;
}

.task-count.complete {
  color: #67c23a;
}

.task-card-status .fa-chevron-right {
  color: #909399;
  font-size: 14px;
}

.lock-icon {
  color: #909399;
  font-size: 14px;
}

/* Detail View */
.detail-content {
  padding: 1rem;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.back-btn {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.detail-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.client-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.client-task-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.client-task-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.client-name {
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.task-status {
  font-size: 13px;
}

.task-status.completed {
  color: #67c23a;
}

.task-status.pending {
  color: #e6a23c;
}

.task-status.locked {
  color: #909399;
  font-style: italic;
}

.task-status.did-not-attend {
  color: #f56c6c;
  font-weight: 500;
}

.task-action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
}

.task-address {
  font-size: 13px;
  color: #c0c4cc;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.task-address i {
  color: #6BA6CD;
}

.map-link-btn {
  color: #6BA6CD !important;
  padding: 0 !important;
  font-size: 12px;
}

.map-link-btn:hover {
  color: #8BC4E0 !important;
}

.task-geocode {
  font-size: 12px;
  color: #909399;
}

.task-geocode i {
  margin-right: 0.25rem;
  color: #6BA6CD;
}

.adjust-time-btn {
  padding: 0;
  margin-left: 0.5rem;
}

.time-edit-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.time-edit-row .el-date-editor {
  width: 130px;
}

.time-edit-row .el-button + .el-button {
  margin-left: 0;
}

/* Location Info Card */
.location-info-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
}

.location-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.location-label {
  color: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
}

.location-label i {
  color: #6BA6CD;
  margin-right: 0.5rem;
}

.location-value {
  color: #e0e0e0;
  font-size: 0.9rem;
  text-align: right;
  word-break: break-all;
}

.location-pending {
  color: #b0b0b0;
  font-size: 0.9rem;
}

/* Shift Notes Styles */
.notes-loading {
  text-align: center;
  padding: 2rem;
  color: #909399;
  font-size: 15px;
}

.notes-client-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.notes-client-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.notes-client-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.notes-client-header:hover {
  background: rgba(255, 255, 255, 0.05);
}

.notes-client-header i:first-child {
  color: #909399;
  font-size: 12px;
  width: 16px;
  text-align: center;
}

.ml-auto {
  margin-left: auto;
}

.notes-client-body {
  padding: 0 1rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.notes-section {
  margin-top: 1rem;
}

.notes-label {
  display: block;
  font-size: 14px;
  color: #c0c4cc;
  margin-bottom: 0.5rem;
}

.required-mark {
  color: #f56c6c;
}

.yes-no-buttons {
  display: flex;
  gap: 0.5rem;
}

.notes-save-row {
  margin-top: 1rem;
  text-align: right;
}

/* Medication Administration Styles */
.medication-admin-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 0.75rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}
</style>
